import { useMemo, useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { MONTHS } from '../constants/dashboardConstants';
import { leadService } from '@/pages/verve-leads/leads/services/leadService';
import { verveContactService } from '@/services/VerveContactService';
import { getLeadExecutions } from '@/pages/lead_executions/services/leadExecutionService';
import API from '@/lib/API';

// Compute from/to Date boundaries from the filters object
const getDateBounds = (filters = {}) => {
  const now = new Date();
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOf   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  // Custom From/To range takes priority over preset buttons
  if (filters.fromDate || filters.toDate) {
    return {
      from: filters.fromDate ? startOf(new Date(filters.fromDate)) : null,
      to:   filters.toDate   ? endOf(new Date(filters.toDate))     : null,
    };
  }
  switch (filters.date) {
    case 'today':      return { from: startOf(now), to: endOf(now) };
    case 'this_week': {
      const day = now.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day;
      const mon = new Date(now);
      mon.setDate(now.getDate() + diffToMon);
      return { from: startOf(mon), to: endOf(now) };
    }
    case 'this_month': return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOf(now) };
    case 'this_year':  return { from: new Date(now.getFullYear(), 0, 1), to: endOf(now) };
    default:           return { from: null, to: null };
  }
};

const isInRange = (dateVal, from, to) => {
  if (!dateVal) return false;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return false;
  if (from && d < from) return false;
  if (to   && d > to)   return false;
  return true;
};

export const useDashboard = (searchQuery = "", filters = {}) => {
  const { user } = useAuthStore();
  const [data, setData] = useState({ leads: [], contacts: [], leadsStats: {}, received: [], expenses: [], users: [], sales: [], leadExecutions: [], generalExpenses: [], inventoryItems: [], purchaseReturns: [], saleReturns: [], damagedItems: [], purchases: [], openingStocks: [] });
  const [loading, setLoading] = useState(true);

  const filteredData = useMemo(() => {
    let leads    = Array.isArray(data.leads)    ? [...data.leads]    : [];
    let contacts = Array.isArray(data.contacts) ? [...data.contacts] : [];
    let received = Array.isArray(data.received) ? [...data.received] : [];
    let expenses = Array.isArray(data.expenses) ? [...data.expenses] : [];
    let sales    = Array.isArray(data.sales)    ? [...data.sales]    : [];
    let budgets  = Array.isArray(data.budgets)  ? [...data.budgets]  : [];
    let leadExecutions = Array.isArray(data.leadExecutions) ? [...data.leadExecutions] : [];
    let generalExpenses = Array.isArray(data.generalExpenses) ? [...data.generalExpenses] : [];

    // ── 1. Date range filtering ────────────────────────────────────
    const { from, to } = getDateBounds(filters);
    if (from || to) {
      leads    = leads.filter(l => isInRange(l.createdAt, from, to));
      contacts = contacts.filter(c => isInRange(c.createdAt, from, to));
      received = received.filter(r => isInRange(r.paymentDate || r.createdAt, from, to));
      expenses = expenses.filter(e => isInRange(e.createdAt, from, to));
      generalExpenses = generalExpenses.filter(e => isInRange(e.createdAt, from, to));
      sales    = sales.filter(s => isInRange(s.date || s.createdAt, from, to));
      leadExecutions = leadExecutions.filter(l => isInRange(l.createdAt, from, to));
      generalExpenses = generalExpenses.filter(e => isInRange(e.createdAt, from, to));
    }

    // ── 2. Search (Verve ID) filtering ────────────────────────────
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      leads = leads.filter(l => {
        const id = (l.customId || l.projectId || l.projectNumber || l.leadId || l._id || '').toLowerCase();
        return id.includes(q);
      });
      const validLeadIds = new Set(leads.map(l => String(l._id)).filter(id => id && id !== 'undefined'));

      received = received.filter(r => {
        const rid = String(r.relatedProjectId?._id || r.relatedProjectId);
        return rid && rid !== 'undefined' && validLeadIds.has(rid);
      });

      expenses = expenses.filter(e => {
        const eid = String(e.relatedProjectId?._id || e.relatedProjectId);
        return eid && eid !== 'undefined' && validLeadIds.has(eid);
      });

      sales = sales.filter(s => {
        const projId = String(s.project?._id || s.project || s.projectId?._id || s.projectId);
        if (projId && projId !== 'undefined' && validLeadIds.has(projId)) return true;
        if (!s.projectNumber) return false;
        return leads.some(l => (l.customId && l.customId === s.projectNumber) || (l.projectId && l.projectId === s.projectNumber));
      });
    }

    return { leads, contacts, received, expenses, sales, leadExecutions, generalExpenses };
  }, [data, searchQuery, filters]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const safeFetch = (promise) => promise.catch(err => { console.error('Fetch error:', err); return []; });

        const [leadsRes, contactsRes, receivedRes, expensesRes, usersRes, salesRes, projectExpensesRes, leadExecutionsRes, inventoryRes, purchaseReturnsRes, saleReturnsRes, damagedRes, purchasesRes, openingStocksRes] = await Promise.all([
          safeFetch(leadService.getLeads({ showAll: true, limit: 10000 })),
          safeFetch(verveContactService.getContacts({ showAll: true, limit: 10000 })),
          safeFetch(API.get('/received-amounts', { params: { relatedProjectCollection: 'VerveContact' } })),
          safeFetch(API.get('/general-expenses')),
          safeFetch(API.get('/teams')),
          safeFetch(API.get('/inventory/sales', { params: { limit: 10000, serviceType: 'Verve' } })),
          safeFetch(API.get('/verve-projects/all-expenses')),
          safeFetch(getLeadExecutions()),
          safeFetch(API.get('/inventory', { params: { serviceType: 'Verve', limit: 10000 } })),
          safeFetch(API.get('/inventory/purchase-returns', { params: { serviceType: 'Verve', limit: 10000 } })),
          safeFetch(API.get('/inventory/sale-returns', { params: { serviceType: 'Verve', limit: 10000 } })),
          safeFetch(API.get('/inventory/damaged-items', { params: { serviceType: 'Verve' } })),
          safeFetch(API.get('/final-inventory/purchases', { params: { serviceType: 'Verve', limit: 10000 } })),
          safeFetch(API.get('/inventory/opening-stock', { params: { serviceType: 'Verve', limit: 10000 } }))
        ]);
        
        const parseArray = (res) => {
          if (Array.isArray(res)) return res;
          if (res?.data && Array.isArray(res.data)) return res.data;
          if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
          if (res?.data?.leads && Array.isArray(res.data.leads)) return res.data.leads;
          if (res?.data?.data?.leads && Array.isArray(res.data.data.leads)) return res.data.data.leads;
          if (res?.data?.teams && Array.isArray(res.data.teams)) return res.data.teams;
          if (res?.data?.data?.teams && Array.isArray(res.data.data.teams)) return res.data.data.teams;
          if (res?.data?.sales && Array.isArray(res.data.sales)) return res.data.sales;
          if (res?.data?.data?.sales && Array.isArray(res.data.data.sales)) return res.data.data.sales;
          if (res?.contacts && Array.isArray(res.contacts)) return res.contacts;
          if (res?.data?.contacts && Array.isArray(res.data.contacts)) return res.data.contacts;
          if (res?.data?.data?.contacts && Array.isArray(res.data.data.contacts)) return res.data.data.contacts;

          // generic fallback to find any array inside data
          if (res && typeof res === 'object') {
            const arr = Object.values(res).find(v => Array.isArray(v));
            if (arr) return arr;
          }
          if (res?.data && typeof res.data === 'object') {
            const arr = Object.values(res.data).find(v => Array.isArray(v));
            if (arr) return arr;
          }
          return [];
        };

        const genExpenses = parseArray(expensesRes);
        const projExpenses = parseArray(projectExpensesRes);

        setData({
          leads: parseArray(leadsRes),
          leadsStats: leadsRes?.data?.stats || leadsRes?.stats || {},
          contacts: parseArray(contactsRes),
          received: parseArray(receivedRes),
          expenses: [...genExpenses, ...projExpenses],
          generalExpenses: genExpenses,
          users: parseArray(usersRes),
          sales: parseArray(salesRes),
          leadExecutions: parseArray(leadExecutionsRes),
          inventoryItems: parseArray(inventoryRes),
          purchaseReturns: parseArray(purchaseReturnsRes),
          saleReturns: parseArray(saleReturnsRes),
          damagedItems: parseArray(damagedRes),
          purchases: parseArray(purchasesRes),
          openingStocks: parseArray(openingStocksRes)
        });
      } catch (err) {
        console.error("Dashboard fetch error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const analytics = useMemo(() => {
    const leads = Array.isArray(filteredData.leads) ? filteredData.leads : [];
    const received = Array.isArray(filteredData.received) ? filteredData.received : [];
    const expenses = Array.isArray(filteredData.expenses) ? filteredData.expenses : [];
    const generalExpenses = Array.isArray(filteredData.generalExpenses) ? filteredData.generalExpenses : [];
    const sales = Array.isArray(filteredData.sales) ? filteredData.sales : [];
    const leadExecutions = Array.isArray(filteredData.leadExecutions) ? filteredData.leadExecutions : [];
    const contacts = Array.isArray(filteredData.contacts) ? filteredData.contacts : [];
    
    // Calculate total sales from the inventory sales API
    const totalSales = sales.reduce((sum, item) => sum + (Number(item.netPayable || item.netBill || item.totalAmount || item.amount) || 0), 0);
    
    // Calculate total project revenue from received payments
    const totalProjectRevenue = received.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    
    // Total combined revenue
    const totalRevenue = totalSales + totalProjectRevenue;

    // Use approvedAmount if available, fallback to requested amount
    const totalExpenses = expenses.reduce((sum, item) => sum + (Number(item.approvedAmount || item.amount) || 0), 0);
    const materialCosts = expenses.filter(item => item.category && item.category.toLowerCase().includes('material')).reduce((sum, item) => sum + (Number(item.approvedAmount || item.amount) || 0), 0);
    
    const netProfit = totalRevenue - totalExpenses;

    const totalPipelineValue = Number(data.leadsStats?.budget) || leads.reduce((sum, l) => sum + (Number(l.projectValue) || Number(l.quotationAmount) || Number(l.budget) || 0), 0);

    // A lead is considered a project if isMovedToProject is true OR leadStatus is in project stages
    const projectStages = ['cust_acceptance', 'design', 'design_approval', 'final_quotation', 'quotation_approval', 'documents', 'project_started', 'project', 'customer_acceptance'];
    const projects = leads.filter(l => l.isMovedToProject === true || l.isMovedToProject === 'true' || projectStages.includes(l.leadStatus) || projectStages.includes(l.status));
    const activeProjectsCount = projects.filter(p => !['completed', 'drop', 'cancelled'].includes(p.projectStatus || p.status)).length;
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const newLeads = leads.filter(l => {
      const d = new Date(l.createdAt);
      const isProj = l.isMovedToProject === true || l.isMovedToProject === 'true' || projectStages.includes(l.leadStatus) || projectStages.includes(l.status);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear && !isProj;
    }).length;

    const conversionRate = data.leadsStats?.rate || (leads.length > 0 ? ((projects.length / leads.length) * 100).toFixed(1) : 0);

    const monthlyRevenue = received.filter(r => {
      const d = new Date(r.paymentDate || r.createdAt);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

    const leadsBySource = leads.reduce((acc, l) => {
      const src = l.source || 'Other';
      acc[src] = (acc[src] || 0) + 1;
      return acc;
    }, {});

    const activeContacts = filteredData.contacts?.filter(c => !c.isDisabled) || [];
    const totalContacts = activeContacts.length;
    const totalLeads = Number(data.leadsStats?.converted) || leads.length; 
    const totalProjects = projects.length;
    const completedProjects = projects.filter(p => p.status === 'completed' || p.projectStatus === 'completed').length;

    const users = Array.isArray(data.users) ? data.users : [];
    const usersMap = {};
    users.forEach(u => {
      usersMap[u._id] = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
    });

    const teamMap = {};
    
    // Initialize with all sales executives / field marketing
    users.forEach(u => {
      if (u.role === 'sales_executive' || u.role === 'field_marketing' || u.designation === 'Sales Executive' || u.designation === 'Field Marketing') {
        teamMap[u._id] = { 
          _id: u._id, 
          name: usersMap[u._id], 
          phone: u.phoneNumber || u.phone || '',
          email: u.email || '',
          totalLeads: 0, 
          converted: 0, 
          revenue: 0,
          pending: 0,
          pipeline: 0,
          completed: 0
        };
      }
    });

    leads.forEach(l => {
      let assignedId = 'unassigned';
      let assignedName = 'Unassigned Leads';
      
      const assigned = l.assignedTo || l.salesPerson || l.telecaller;
      if (assigned && assigned._id) {
        assignedId = assigned._id;
        assignedName = assigned.name || usersMap[assigned._id] || 'Unknown';
      } else if (typeof assigned === 'string' && assigned.length > 0) {
        assignedId = assigned;
        assignedName = usersMap[assigned] || 'Sales Member';
      }

      if (!teamMap[assignedId]) {
        const u = users.find(usr => String(usr._id) === String(assignedId)) || {};
        teamMap[assignedId] = { 
          _id: assignedId, 
          name: assignedName, 
          phone: u.phoneNumber || u.phone || '',
          email: u.email || '',
          totalLeads: 0, 
          converted: 0, 
          revenue: 0,
          pending: 0,
          pipeline: 0,
          completed: 0
        };
      }
      teamMap[assignedId].totalLeads += 1;

      const isProject = projectStages.includes(l.leadStatus) || projectStages.includes(l.status) || l.isMovedToProject === true || l.isMovedToProject === 'true';
      const isCompleted = l.status === 'completed' || l.projectStatus === 'completed';

      if (isProject) {
        teamMap[assignedId].converted += 1;
        if (isCompleted) {
          teamMap[assignedId].completed += 1;
        } else {
          teamMap[assignedId].pipeline += 1;
        }
      } else {
        teamMap[assignedId].pending += 1;
      }
    });

    received.forEach(r => {
      // Find the lead this payment belongs to
      const leadId = r.relatedProjectId?._id || r.relatedProjectId;
      if (leadId) {
        const lead = leads.find(l => String(l._id) === String(leadId));
        if (lead) {
          let assignedId = 'unassigned';
          const assigned = lead.assignedTo || lead.salesPerson || lead.telecaller;
          if (assigned && assigned._id) {
            assignedId = assigned._id;
          } else if (typeof assigned === 'string' && assigned.length > 0) {
            assignedId = assigned;
          }
          if (teamMap[assignedId]) {
            teamMap[assignedId].revenue += (Number(r.amount) || 0);
          }
        }
      }
    });
    
    const salesTeamData = Object.values(teamMap).filter(member => {
      // Find the corresponding user to check their role
      const userObj = users.find(u => String(u._id) === String(member._id));
      return userObj && (['sales_executive', 'field_marketing'].includes(userObj.role) || ['Sales Executive', 'Field Marketing'].includes(userObj.designation));
    }).sort((a, b) => {
      if (b.converted !== a.converted) return b.converted - a.converted; // Sort primarily by converted leads
      const rateA = a.totalLeads > 0 ? a.converted / a.totalLeads : 0;
      const rateB = b.totalLeads > 0 ? b.converted / b.totalLeads : 0;
      return rateB - rateA; // Tie breaker: conversion rate
    }).slice(0, 5); // Take top 5

    // Custom Lead Execution Metrics for Dashboard
    const totalPipelineValueFromExecutions = leadExecutions.reduce((acc, curr) => {
      return acc + (Number(curr.projectValue) || Number(curr.leadId?.quotationAmount) || Number(curr.leadId?.budget) || 0);
    }, 0);

    const executionLeadStages = [
      'budget', 'ordering', 'sign off copy', 'payments', 
      'execution not started', 'not started', 'converted', 
      'pending_approval', 'initial_quotation', 'initial_design'
    ];
    let totalExecutionsLeads = 0;
    let totalExecutionsProjects = 0;

    leadExecutions.forEach(ex => {
      const stage = (ex.leadId?.leadStatus || ex.leadId?.status || ex.status || '').toLowerCase().trim();
      // If it's empty, we might count it as a lead if it just started
      if (!stage || executionLeadStages.includes(stage)) {
        totalExecutionsLeads++;
      } else {
        totalExecutionsProjects++;
      }
    });

    const totalClients = contacts.length;

    return {
      totalSales,
      totalProjectRevenue,
      totalRevenue,
      totalExpenses,
      netProfit,
      totalPipelineValue,
      totalPipelineValueFromExecutions,
      totalProjects: activeProjectsCount,
      totalExecutionsProjects,
      totalExecutionsLeads,
      totalClients,
      newLeads,
      conversionRate,
      monthlyRevenue,
      leadsBySource,
      materialCosts,
      totalProjects,
      completedProjects,
      salesTeam: salesTeamData,
      generalExpenses
    };
  }, [filteredData, data.users]);

  // Build Sales/Expenses/Revenue chart parameters dynamically (Last 13 Months)
  const revenueChartData = useMemo(() => {
    const received = Array.isArray(filteredData.received) ? filteredData.received : [];
    const expenses = Array.isArray(filteredData.expenses) ? filteredData.expenses : [];
    const sales = Array.isArray(filteredData.sales) ? filteredData.sales : [];

    const labels = [];
    const salesData = new Array(13).fill(0);
    const expensesData = new Array(13).fill(0);
    const revenueData = new Array(13).fill(0);

    const now = new Date();
    // Generate last 13 months
    for (let i = 12; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = d.toLocaleString('default', { month: 'short' });
      labels.push(`${d.getFullYear()} ${monthName}`);
    }

    const getMonthIndex = (dateString) => {
      if (!dateString) return -1;
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return -1;
      const monthsDiff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (monthsDiff >= 0 && monthsDiff <= 12) {
        return 12 - monthsDiff;
      }
      return -1;
    };

    sales.forEach(s => {
      const idx = getMonthIndex(s.date || s.createdAt);
      if (idx !== -1) {
        salesData[idx] += (Number(s.netPayable || s.netBill || s.totalAmount || s.amount) || 0);
      }
    });

    expenses.forEach(e => {
      const idx = getMonthIndex(e.transactionDate || e.createdAt);
      if (idx !== -1) {
        expensesData[idx] += (Number(e.approvedAmount || e.amount) || 0);
      }
    });

    received.forEach(r => {
      const idx = getMonthIndex(r.paymentDate || r.transactionDate || r.createdAt);
      if (idx !== -1) {
        revenueData[idx] += (Number(r.amount) || 0);
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Blum Sales',
          data: salesData,
          borderColor: '#7ba2bf',
          backgroundColor: 'rgba(123, 162, 191, 0.18)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#7ba2bf',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Project Revenue',
          data: revenueData,
          borderColor: '#d1d5db',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#cbd5e1',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Total Expenses',
          data: expensesData,
          borderColor: '#0f172a',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#0f172a',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ],
    };
  }, [filteredData]);

  // Build Lead source parameters dynamically
  const leadSourceData = useMemo(() => {
    const sources = Object.keys(analytics.leadsBySource);
    const counts = Object.values(analytics.leadsBySource);
    return {
      labels: sources.length > 0 ? sources : ['Website', 'Phone', 'Walk-in', 'Social', 'Referral'],
      datasets: [{
        data: counts.length > 0 ? counts : [0, 0, 0, 0, 0],
        backgroundColor: ['#0f172a', '#334155', '#64748b', '#94a3b8', '#cbd5e1', '#f1f5f9', '#e2e8f0'],
        borderWidth: 0,
      }],
    };
  }, [analytics.leadsBySource]);

  // Get filtered lists for display
  const recentLeads = useMemo(() => {
    const leads = Array.isArray(filteredData.leads) ? filteredData.leads : [];
    return [...leads]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10);
  }, [filteredData.leads]);

  const activeProjects = useMemo(() => {
    const leads = Array.isArray(filteredData.leads) ? filteredData.leads : [];
    const projectStages = ['cust_acceptance', 'design', 'design_approval', 'final_quotation', 'quotation_approval', 'documents', 'project_started', 'project', 'customer_acceptance', 'Pending Store', 'pending store'];
    return leads
      .filter(l => {
         const isProj = l.isMovedToProject === true || l.isMovedToProject === 'true' || projectStages.includes(l.leadStatus) || projectStages.includes(l.status);
         const isActive = !['completed', 'drop', 'cancelled'].includes(l.projectStatus || l.status);
         return isProj && isActive;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10);
  }, [filteredData.leads]);

  const allProjects = useMemo(() => {
    const leads = Array.isArray(filteredData.leads) ? filteredData.leads : [];
    const projectStages = ['cust_acceptance', 'design', 'design_approval', 'final_quotation', 'quotation_approval', 'documents', 'project_started', 'project', 'customer_acceptance'];
    return leads.filter(l => l.isMovedToProject === true || l.isMovedToProject === 'true' || projectStages.includes(l.leadStatus) || projectStages.includes(l.status));
  }, [filteredData.leads]);

  // Build monthly leads chart data
  const monthlyLeadsData = useMemo(() => {
    const leads = Array.isArray(filteredData.leads) ? filteredData.leads : [];
    
    const labels = [];
    const leadsDataArr = new Array(13).fill(0);
    const now = new Date();
    
    // Generate last 13 months
    for (let i = 12; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = d.toLocaleString('default', { month: 'short' });
      labels.push(`${d.getFullYear()} ${monthName}`);
    }

    const getMonthIndex = (dateString) => {
      if (!dateString) return -1;
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return -1;
      const monthsDiff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (monthsDiff >= 0 && monthsDiff <= 12) {
        return 12 - monthsDiff;
      }
      return -1;
    };

    leads.forEach(l => {
      const idx = getMonthIndex(l.createdAt);
      if (idx !== -1) {
        leadsDataArr[idx] += 1;
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Leads',
          data: leadsDataArr,
          backgroundColor: '#f9a825',
          borderRadius: 6,
        },
      ],
    };
  }, [filteredData.leads]);

  const monthlySalesBarData = useMemo(() => {
    const budgets = Array.isArray(filteredData.budgets) ? filteredData.budgets : [];
    
    const labels = [];
    const directSalesData = new Array(6).fill(0);
    const projectSalesData = new Array(6).fill(0);
    const now = new Date();
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = d.toLocaleString('default', { month: 'short' });
      labels.push(monthName); // Just "Feb", "Mar", etc.
    }

    const getMonthIndex = (dateString) => {
      if (!dateString) return -1;
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return -1;
      const monthsDiff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (monthsDiff >= 0 && monthsDiff <= 5) {
        return 5 - monthsDiff;
      }
      return -1;
    };

    budgets.forEach(row => {
      // Check if conformed
      const hasQuoteNo = !!row.quotationNo;
      const isOrderingQuote = row.status?.toLowerCase().includes('orderings') || row.type?.toLowerCase().includes('orderings');
      const hasExplicitConfirm = row.isConfirmOrder !== undefined || row.isConformOrder !== undefined;

      if (!hasQuoteNo || (!isOrderingQuote && !hasExplicitConfirm)) {
        return; // Not Started
      }

      const isChecked = row.isConfirmOrder !== false && row.isConformOrder !== false && row.isConfirmOrder !== 'false' && row.isConformOrder !== 'false';
      
      if (isChecked) {
        const idx = getMonthIndex(row.date || row.createdAt);
        if (idx !== -1) {
          const amt = Number(row.totals?.grandTotal || row.amount || row.grandTotal || 0);
          const saleType = String(row.saleType || '').toLowerCase();
          
          if (saleType.includes('direct')) {
            directSalesData[idx] += amt;
          } else {
            projectSalesData[idx] += amt;
          }
        }
      }
    });

    return {
      labels,
      datasets: [
        {
          label: "Direct Appliance Sales (₹)",
          data: directSalesData,
          backgroundColor: "rgba(13, 148, 136, 0.85)", // Teal
          borderColor: "rgba(13, 148, 136, 1)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.6,
        },
        {
          label: "Project-Related Sales (₹)",
          data: projectSalesData,
          backgroundColor: "rgba(217, 119, 6, 0.85)", // Gold/Amber
          borderColor: "rgba(217, 119, 6, 1)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.6,
        }
      ]
    };
  }, [filteredData.budgets]);

  const inventoryChartData = useMemo(() => {
    const totalStock = data.inventoryItems.reduce((sum, i) => sum + (Number(i.stock) || 0), 0);
    const totalSalesQty = data.sales.reduce((sum, s) => sum + (s.items || []).reduce((a, it) => a + (Number(it.qty) || 0), 0), 0);
    const totalPurchaseReturns = data.purchaseReturns.reduce((sum, r) => sum + (Number(r.totalAmount) || (r.items || []).reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0)), 0);
    const totalSaleReturns = data.saleReturns.reduce((sum, r) => sum + (Number(r.totalAmount) || (r.items || []).reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0)), 0);
    const totalDamaged = data.damagedItems.reduce((sum, d) => sum + (Number(d.totalDamagedValue) || (d.damagedItems || []).reduce((a, it) => a + (Number(it.damagedQty) || 0) * (Number(it.rate) || 0), 0)), 0);
    const totalOpeningStock = data.openingStocks.reduce((sum, o) => sum + (Number(o.quantity) || Number(o.availableQty) || Number(o.stock) || 0), 0);

    return {
      labels: ["Total Inventory", "Total Sales", "Purchase Returns", "Sale Returns", "Damaged", "Open Stock"],
      datasets: [{
        label: "Units / Level",
        data: [totalStock, totalSalesQty, totalPurchaseReturns, totalSaleReturns, totalDamaged, totalOpeningStock],
        backgroundColor: "rgba(13, 148, 136, 0.8)",
        hoverBackgroundColor: "rgba(13, 148, 136, 1)",
        borderRadius: 6,
        barThickness: 16
      }]
    };
  }, [data.inventoryItems, data.sales, data.purchaseReturns, data.saleReturns, data.damagedItems, data.openingStocks]);

  return {
    analytics,
    revenueChartData,
    leadSourceData,
    monthlyLeadsData,
    monthlySalesBarData,
    inventoryChartData,
    recentLeads,
    activeProjects,
    allLeads: Array.isArray(filteredData.leads) ? filteredData.leads : [],
    allProjects,
    users: data.users,
    sales: Array.isArray(filteredData.sales) ? filteredData.sales : [],
    received: Array.isArray(filteredData.received) ? filteredData.received : [],
    generalExpenses: Array.isArray(filteredData.generalExpenses) ? filteredData.generalExpenses : [],
    purchases: Array.isArray(data.purchases) ? data.purchases : [],
    loading
  };
};
